import type { CSSProperties, ReactElement, ReactNode } from 'react';
import React from 'react';
import styled from 'types/theme.types';
import { FormattedMessage } from 'react-intl';
import { Skeleton } from 'components/common/skeleton';
import Alert from 'components/common/feedback/alert';
import { css } from '@emotion/react';
import Link from 'components/common/link';
import { type Env0Theme } from 'constants/themes.constants';
import { typeStyle } from 'constants/typography';

export const headerPaddingCss = css`
  padding: 15px 24px;
  position: relative;
`;

interface Props {
  id?: string;
  title?: ReactElement | ReactNode;
  style?: CSSProperties;
  inline?: boolean;
  inlineBottomBorder?: boolean;
  /** true = full-bleed hairline under the title, 'inset' = the hairline stops at the card padding
   *  (the Plan & limits treatment in DES-155), false = none. */
  withTitleDivider?: boolean | 'inset';
  loading?: boolean;
  className?: string;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  allowBodyScroll?: boolean;
  fullWidthContent?: boolean;
  vertical?: boolean;
  itemGap?: number | string;
  children?: ReactNode;
  /** Lifted off antd Card. Hover moves the border, never a shadow. */
  hoverable?: boolean;
  /** antd Card's cover slot: rendered between the header and the body. */
  cover?: ReactNode;
  /** antd Card's bodyStyle.padding. */
  bodyPadding?: string;
}

/**
 * Each style group below is a props-taking function rather than nested inside a css block: an
 * interpolation inside css receives only the theme, not the component's props. Same shape
 * buttons-container.tsx uses for its isInline split.
 */
type ContainerProps = { inline?: boolean; inlineBottomBorder?: boolean; hoverable?: boolean; fullBleed?: boolean };
type ContainerArgs = ContainerProps & { theme: Env0Theme };

const containerCss = (props: ContainerArgs) => css`
  /* antd's card root is position: relative, and three former raw-antd call sites put an absolutely
         positioned info icon inside the card and relied on it for their containing block. Without this the
         icon escapes to whatever ancestor is positioned and lands at the far edge of the page. */
  position: relative;
  ${
    props.inline
      ? props.inlineBottomBorder
        ? ''
        : `border-block-end: ${props.theme.borderWidth.xs} solid var(--border-primary)`
      : `border: ${props.theme.borderWidth.xs} solid var(--border-primary)`
  };
  border-radius: ${props.inline ? props.theme.radius.none : props.theme.radius['400']};
  background: var(--surface-main-cards);
  /* Full-bleed bodies (fullWidthContent) put square-cornered panels flush against the card edge —
         the inner box painted straight over the 12px border arc, which read as a missing corner
         (UI-polish review, item C). Clipping is scoped to full-bleed so the absolutely-positioned
         info icons other call sites hang outside the card keep escaping. */
  ${props.fullBleed ? 'overflow: hidden;' : ''}

  /* No card shadow exists in the token set or on any Figma frame, so hover moves the border colour.
         antd's own .ant-card-hoverable:hover raises a three-layer rgba shadow instead - not reachable
         from the bridge, which is why hover is owned here. */
  ${
    props.hoverable
      ? css`
          cursor: pointer;
          &:hover {
            border-color: var(--border-action);
          }
        `
      : ''
  }
`;

const Container = styled('div')<ContainerProps>`
  display: flex;
  flex-direction: column;
  ${containerCss}
`;

type HeaderArgs = { divider: boolean | 'inset'; theme: Env0Theme };

// An ::after, so the header keeps its full-width hit area while the hairline stops at the padding.
const insetDividerCss = (color: string, inline: string) => css`
  &::after {
    content: '';
    position: absolute;
    right: ${inline};
    bottom: 0;
    left: ${inline};
    border-bottom: 1px solid ${color};
  }
`;

const headerCss = (props: HeaderArgs) => css`
  position: relative;
  /* The frames measure 20px top and 22px inline; 22 is not a rung on the ladder, so the inline pair
         rounds up to the nearest one. Raised with design as a Figma-side fix. */
  padding: ${props.theme.padding.xl} ${props.theme.padding.xxl};
  /* Heading-h3, per every card title in the frames (267:7840 "Environment Details"). */
  ${typeStyle.headingH3}
  color: var(--text-heading);
  border-block-end: ${props.divider === true ? `${props.theme.borderWidth.xs} solid var(--border-primary)` : 'none'};
  ${props.divider === 'inset' ? insetDividerCss('var(--border-primary)', props.theme.padding.xxl) : ''}
  /* Without a divider the frames are one padded stack with 16px item spacing, not two padded
         halves — the header sheds its bottom padding and the body opens with the lg rung. */
  ${props.divider === false ? 'padding-block-end: 0;' : ''}
`;

export const Header = styled.div<{ divider: boolean | 'inset' }>`
  ${headerCss}
`;

type BodyProps = {
  allowBodyScroll?: boolean;
  noPadding?: boolean;
  tightTop?: boolean;
  itemGap: Props['itemGap'];
  vertical?: boolean;
  bodyPadding?: string;
};
type BodyArgs = BodyProps & { theme: Env0Theme };

/** bodyPadding is for call sites that would otherwise override .ant-card-body's padding. */
const bodyPaddingCss = (props: BodyArgs) => css`
  ${props.noPadding ? '' : `padding: ${props.bodyPadding ?? `${props.theme.padding.xl} ${props.theme.padding.xxl}`};`}
  ${props.tightTop && !props.noPadding && !props.bodyPadding ? `padding-block-start: ${props.theme.spacing.lg};` : ''}
`;

const bodyGapCss = (props: BodyArgs) =>
  props.itemGap
    ? css`
        gap: ${props.theme.spacing.lg};
      `
    : css``;

const Body = styled.div<BodyProps>`
  ${bodyPaddingCss}
  ${props => props.allowBodyScroll && 'overflow-y: auto;'}
  ${props => props.itemGap && 'display: flex;'}
  ${bodyGapCss}
  ${props => props.vertical && 'flex-direction: column;'}
`;

const Card: React.FC<Props> = ({
  id,
  title,
  children,
  style,
  inline,
  inlineBottomBorder = false,
  withTitleDivider = true,
  loading,
  allowBodyScroll = false,
  fullWidthContent,
  itemGap,
  vertical,
  hoverable,
  cover,
  bodyPadding,
  ...props
}) => {
  return (
    <Container
      id={id}
      style={style}
      inline={inline}
      inlineBottomBorder={inline && inlineBottomBorder}
      hoverable={hoverable}
      fullBleed={fullWidthContent}
      {...props}>
      {title && <Header divider={withTitleDivider}>{title}</Header>}
      {cover}
      <Body
        className="custom-card-body"
        allowBodyScroll={allowBodyScroll}
        noPadding={fullWidthContent}
        tightTop={Boolean(title) && withTitleDivider === false}
        itemGap={itemGap}
        vertical={vertical}
        bodyPadding={bodyPadding}>
        {loading ? <Skeleton /> : children}
      </Body>
    </Container>
  );
};

interface CardHeaderProps {
  titleId: string;
  titleValues?: Record<string, React.ReactNode>;
  /** Optional: a header can be a bare title-plus-action row (the keys and API-keys cards). */
  descriptionId?: string;
  descriptionValues?: Record<string, React.ReactNode | ((parts: string | ReactNode) => ReactNode)>;
  link?: string;
  linkMessageId?: string;
  warning?: string;
  descriptionExtensionComponent?: React.ReactElement; // Used to display additional information in the description
  headerAction?: React.ReactNode;
}

export const CardHeader: React.FC<CardHeaderProps> = ({
  titleId,
  titleValues,
  descriptionId,
  descriptionValues,
  link,
  linkMessageId = 'link.explanation.more',
  warning,
  descriptionExtensionComponent,
  headerAction,
  ...props
}) => {
  return (
    <CardHeaderContainer style={{ width: '100%' }} {...props}>
      <TextContent>
        {/* react-intl renders bare text, so the hook callers can restyle the title through lives on
            the container - FormattedMessage swallows its own data-e2e. */}
        <TitleContainer data-e2e="card-header-title">
          <FormattedMessage data-e2e="card-header-text" id={titleId} values={titleValues} />
          {/* The header action centres against the whole text block, not this row. */}
        </TitleContainer>
        {(descriptionId || warning || descriptionExtensionComponent) && (
          <CardDescription>
            {descriptionId && (
              <>
                <FormattedMessage id={descriptionId} values={descriptionValues} />{' '}
              </>
            )}
            {link && (
              <Link url={link}>
                <FormattedMessage id={linkMessageId} />
              </Link>
            )}
            {warning && <StyledAlert message={<FormattedMessage id={warning} />} showIcon closable />}
            {descriptionExtensionComponent}
          </CardDescription>
        )}
      </TextContent>
      {headerAction && <HeaderActionSlot data-e2e="card-header-action">{headerAction}</HeaderActionSlot>}
    </CardHeaderContainer>
  );
};

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;

  ${({ theme }) => css`
    gap: ${theme.spacing.xxs};
  `}
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const CardDescription = styled.div`
  margin-top: 0;

  color: var(--text-body);

  /* The size and weight are raw values, off the type ramp (A3). */
  font-size: 13px;
  font-weight: 100;
  white-space: pre-wrap;
`;

const StyledAlert = styled(Alert)`
  margin-top: 10px;
`;

const CardHeaderContainer = styled.div`
  width: 100%;
  display: flex;

  ${({ theme }) => css`
    align-items: center;
    gap: ${theme.spacing.xl};
  `}
`;

const HeaderActionSlot = styled.div`
  flex-shrink: 0;
`;

type CardMetaProps = {
  avatar?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  className?: string;
};

/**
 * antd scopes every `.ant-card-meta*` rule as `.ant-card .ant-card-meta*` - card/style/index.js nests the
 * meta block inside `[componentCls]` - and `Meta` itself has no `useStyle`, only `getPrefixCls` and
 * classNames. Card renders a plain div and no `.ant-card` ever mounts, so antd injects nothing and the
 * avatar/title/description would lose their layout entirely. Being inside the card body is not enough;
 * the CSS needs the ancestor.
 *
 * Hence a real seam rather than a re-export: the layout comes from tokens here. Class names mirror antd's
 * shape without borrowing its prefix, matching how Card names `custom-card-body`.
 *
 * Values track antd's genCardMetaStyle: flex row, avatar gutter, detail column takes the remainder, title
 * at the 16px heading style, description in secondary.
 */
const MetaRoot = styled.div`
  display: flex;
`;

const MetaAvatar = styled.div`
  ${({ theme }) => css`
    padding-inline-end: ${theme.spacing.lg};
  `}
`;

const MetaDetail = styled.div`
  overflow: hidden;
  flex: 1;

  ${({ theme }) => css`
    > *:not(:last-child) {
      margin-bottom: ${theme.spacing.xs};
    }
  `}
`;

const MetaTitle = styled.div`
  ${typeStyle.headingH4}
  color: var(--text-heading);
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const MetaDescription = styled.div`
  ${typeStyle.bodyMd}
  color: var(--text-secondary);
`;

export const CardMeta: React.FC<CardMetaProps> = ({ avatar, title, description, className }) => {
  return (
    <MetaRoot className={`card-meta${className ? ` ${className}` : ''}`}>
      {avatar && <MetaAvatar className="card-meta-avatar">{avatar}</MetaAvatar>}
      {(title || description) && (
        <MetaDetail className="card-meta-detail">
          {title && <MetaTitle className="card-meta-title">{title}</MetaTitle>}
          {description && <MetaDescription className="card-meta-description">{description}</MetaDescription>}
        </MetaDetail>
      )}
    </MetaRoot>
  );
};

export default Card;
